import React, { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card, Statistic, Icon, Grid, Header, Segment, List } from 'semantic-ui-react';
import { SG_GET_SLACK_TASK_METRICS, SG_GET_SLACK_MY_DAILY_DIGEST } from 'constants/actions';
import Navigation from './Navigation';
import { useWorkspaceId } from './useWorkspaceId';

function Dashboard() {
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  
  const { taskMetrics, myDailyDigest } = useSelector(
    (s) => s.slack_tasks || {},
    shallowEqual
  );

  useEffect(() => {
    if (!workspaceId) return;
    
    // Fetch metrics and digest on load
    dispatch({
      type: SG_GET_SLACK_TASK_METRICS,
      payload: { workspaceId, queryParams: '' }
    });
    
    dispatch({
      type: SG_GET_SLACK_MY_DAILY_DIGEST,
      payload: { workspaceId, queryParams: '' }
    });
  }, [dispatch, workspaceId]);

  const metrics = taskMetrics || {};
  const digest = myDailyDigest || {};
  const summary = digest.summary || {};
  const tasks = digest.tasks || {};

  return (
    <Page>
      <Navigation />
      
      <Header as="h1">
        <Icon name="slack" />
        Slack Tasks Dashboard
        <Header.Subheader>
          Your task overview and daily digest
        </Header.Subheader>
      </Header>

      {/* Metrics Overview */}
      <Segment>
        <Header as="h3">Overall Metrics</Header>
        <Grid columns={4} stackable>
          <Grid.Column>
            <Statistic size="small">
              <Statistic.Value>
                <Icon name="tasks" color="blue" />
                {metrics.totals?.created || 0}
              </Statistic.Value>
              <Statistic.Label>Created</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="small" color="green">
              <Statistic.Value>
                <Icon name="check circle" />
                {metrics.totals?.completed || 0}
              </Statistic.Value>
              <Statistic.Label>Completed</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="small" color="orange">
              <Statistic.Value>
                <Icon name="spinner" />
                {metrics.totals?.inProgress || 0}
              </Statistic.Value>
              <Statistic.Label>In Progress</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="small">
              <Statistic.Value>
                {metrics.completionRate || '0'}%
              </Statistic.Value>
              <Statistic.Label>Completion Rate</Statistic.Label>
            </Statistic>
          </Grid.Column>
        </Grid>
      </Segment>

      {/* Daily Digest */}
      <Grid columns={2} stackable>
        <Grid.Column>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name="calendar check" color="blue" />
                Today's Tasks
              </Card.Header>
              <Card.Meta>Total: {summary.total || 0} tasks</Card.Meta>
            </Card.Content>
            <Card.Content>
              <TaskSummary>
                <SummaryItem color="#dc2626">
                  <Icon name="exclamation triangle" />
                  <span>{summary.overdue || 0} Overdue</span>
                </SummaryItem>
                <SummaryItem color="#ea580c">
                  <Icon name="clock" />
                  <span>{summary.dueToday || 0} Due Today</span>
                </SummaryItem>
                <SummaryItem color="#2563eb">
                  <Icon name="spinner" />
                  <span>{summary.inProgress || 0} In Progress</span>
                </SummaryItem>
                <SummaryItem color="#64748b">
                  <Icon name="circle outline" />
                  <span>{summary.todo || 0} To Do</span>
                </SummaryItem>
              </TaskSummary>
            </Card.Content>
            <Card.Content extra>
              <Link to="/slacktasks/tasks">
                <Icon name="arrow right" />
                View All Tasks
              </Link>
            </Card.Content>
          </Card>
        </Grid.Column>

        <Grid.Column>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name="fire" color="orange" />
                Priority Breakdown
              </Card.Header>
            </Card.Content>
            <Card.Content>
              <PriorityList>
                <PriorityItem urgent>
                  <Icon name="exclamation circle" />
                  <span>Urgent: {metrics.byPriority?.urgent || 0}</span>
                </PriorityItem>
                <PriorityItem high>
                  <Icon name="arrow up" />
                  <span>High: {metrics.byPriority?.high || 0}</span>
                </PriorityItem>
                <PriorityItem medium>
                  <Icon name="minus" />
                  <span>Medium: {metrics.byPriority?.medium || 0}</span>
                </PriorityItem>
                <PriorityItem low>
                  <Icon name="arrow down" />
                  <span>Low: {metrics.byPriority?.low || 0}</span>
                </PriorityItem>
              </PriorityList>
            </Card.Content>
            <Card.Content extra>
              <Link to="/slacktasks/metrics">
                <Icon name="arrow right" />
                View Detailed Metrics
              </Link>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      {/* Recent Overdue Tasks */}
      {tasks.overdue && tasks.overdue.length > 0 && (
        <Segment color="red">
          <Header as="h3">
            <Icon name="warning sign" color="red" />
            Overdue Tasks
          </Header>
          <List divided>
            {tasks.overdue.slice(0, 5).map((task) => (
              <List.Item key={task.id}>
                <List.Content>
                  <List.Header>{task.title}</List.Header>
                  <List.Description>
                    Due: {task.dueDate || 'No date'} • Priority: {task.priority || 'N/A'}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
          </List>
          {tasks.overdue.length > 5 && (
            <Link to="/slacktasks/tasks?status=overdue">View all {tasks.overdue.length} overdue tasks</Link>
          )}
        </Segment>
      )}

      {/* Quick Actions */}
      <QuickActions>
        <Card.Group itemsPerRow={3} stackable>
          <Card as={Link} to="/slacktasks/create" color="blue">
            <Card.Content textAlign="center">
              <Icon name="plus circle" size="huge" color="blue" />
              <Card.Header style={{ marginTop: '10px' }}>Create New Task</Card.Header>
              <Card.Description>Add a task and notify on Slack</Card.Description>
            </Card.Content>
          </Card>
          
          <Card as={Link} to="/slacktasks/history" color="teal">
            <Card.Content textAlign="center">
              <Icon name="history" size="huge" color="teal" />
              <Card.Header style={{ marginTop: '10px' }}>View History</Card.Header>
              <Card.Description>See completed and past tasks</Card.Description>
            </Card.Content>
          </Card>
          
          <Card as={Link} to="/slacktasks/meetings" color="purple">
            <Card.Content textAlign="center">
              <Icon name="calendar" size="huge" color="purple" />
              <Card.Header style={{ marginTop: '10px' }}>Meetings</Card.Header>
              <Card.Description>View meeting-related tasks</Card.Description>
            </Card.Content>
          </Card>
        </Card.Group>
      </QuickActions>
    </Page>
  );
}

const Page = styled.div`
  padding: 20px;
`;

const TaskSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(p) => p.color};
  font-size: 16px;
  
  i.icon {
    margin: 0;
  }
`;

const PriorityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PriorityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: ${(p) => {
    if (p.urgent) return '#dc2626';
    if (p.high) return '#ea580c';
    if (p.medium) return '#2563eb';
    if (p.low) return '#64748b';
    return '#000';
  }};
  
  i.icon {
    margin: 0;
  }
`;

const QuickActions = styled.div`
  margin-top: 30px;
`;

export default Dashboard;
